import * as Sentry from '@sentry/react'
import {isAxiosError} from 'axios'
import type {Mutation} from '@tanstack/react-query'
import i18n from '@/i18n'
import {claveDeError, errorYaComunicado, translateApiError} from '@/lib/errors'
import {anotarError, patronDeRuta} from '@/lib/bitacora-sesion'
import {publicarAviso} from '@/lib/avisos'
import {redactarUrl} from '@/lib/url-redaction'

/** Toda mutación que falle sin manejo propio se le dice a la persona y llega a Sentry. Solo cubre
 * mutaciones: cada pantalla decide cómo pintar el error de sus lecturas. */

/** Un fallo ya explicado se declara con `marcarErrorComoComunicado(err)` en el `onError`, o con esta
 * meta cuando la mutación entera maneja sus fallos o el manejo no es síncrono. */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** `true` = esta mutación muestra su propio fallo; la red se aparta. */
      manejaError?: boolean
    }
  }
}

type MutacionCualquiera = Mutation<unknown, unknown, unknown, unknown>

export function estadoHttp(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined
}

/** Corta la query entera: un `?search=` lleva lo que la persona escribió, que puede ser una cédula. */
export function rutaDeLaLlamada(error: unknown): string {
  if (!isAxiosError(error)) return 'desconocida'
  const url = error.config?.url
  if (url === undefined || url === '') return 'desconocida'
  return redactarUrl(url.split('?')[0])
}

function reportarASentry(metodo: string, ruta: string, estado: number | undefined): void {
  const patron = patronDeRuta(ruta)
  const codigo = String(estado ?? 'sin_respuesta')
  // Error sintético a propósito: el `AxiosError` lleva los cuerpos (cédulas, datos de salud) y la
  // custodia no puede depender de que `extraErrorDataIntegration` siga apagada.
  const reporte = new Error(`Mutación sin manejo: ${metodo} ${patron} → ${codigo}`)
  reporte.name = 'ErrorDeMutacionSinManejo'
  Sentry.captureException(reporte, {
    // Un 4xx que llega acá es una condición prevista sin manejar, no el servidor roto.
    level: estado !== undefined && estado < 500 ? 'warning' : 'error',
    tags: {origen: 'mutacion_sin_manejo', metodo, estado: codigo},
    contexts: {llamada: {metodo, ruta, estado: estado ?? null}},
    fingerprint: ['mutacion_sin_manejo', metodo, patron, codigo],
  })
}

/** query-core llama primero al `onError` del `MutationCache`. `setTimeout` y no una microtarea porque los
 * manejadores propios cuelgan de `await`s; un manejo asíncrono de verdad necesita `meta.manejaError`. */
function trasLosManejadoresPropios(accion: () => void): void {
  setTimeout(accion, 0)
}

export function manejarErrorDeMutacion(error: unknown, mutation: MutacionCualquiera): void {
  if (mutation.options.meta?.manejaError === true) return

  const estado = estadoHttp(error)
  const metodo = (isAxiosError(error) ? error.config?.method : undefined)?.toUpperCase() ?? 'DESCONOCIDO'
  const ruta = rutaDeLaLlamada(error)
  anotarError({metodo, ruta, status: estado, clave: claveDeError(error)})

  trasLosManejadoresPropios(() => {
    if (errorYaComunicado(error)) return
    // Una sesión vencida no se reporta. Va después del registro para no pisar a quien maneje su 401:
    // un login rechazado también responde 401.
    if (estado === 401) {
      publicarAviso(i18n.t('avisos.sesionVencida.titulo'), i18n.t('avisos.sesionVencida.detalle'))
      return
    }
    reportarASentry(metodo, ruta, estado)
    publicarAviso(i18n.t(tituloSegunMetodo(metodo)), translateApiError(error))
  })
}

/** `POST` se queda en el genérico: sirve tanto para crear como para actos («aprobar»). */
function tituloSegunMetodo(metodo: string): string {
  if (metodo === 'PATCH' || metodo === 'PUT') return 'avisos.accionFallida.guardar'
  if (metodo === 'DELETE') return 'avisos.accionFallida.eliminar'
  return 'avisos.accionFallida.generico'
}
